import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import {
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  FormHelperText,
  Select,
  Checkbox,
  MenuItem,
  FormControlLabel,
  CircularProgress,
  Typography,
  Autocomplete,
  Avatar,
  IconButton,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/js/plugins/colors.min.js";
import "froala-editor/js/plugins/image.min.js";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { BEBaseRoot } from "../../constants/app.constant";
import { resizeProfileImage } from "../../utils/imageUtils";
// import { useSelector } from 'react-redux';
import { getAppTheme } from '../../services/authContext';
import { ImageActions } from '@xeger/quill-image-actions';
import { ImageFormats } from '@xeger/quill-image-formats';
import EmailEditor from "react-email-editor";


Quill.register('modules/imageActions', ImageActions);
Quill.register('modules/imageFormats', ImageFormats);

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script', 'blockquote', 'code-block',
  'list', 'bullet', 'indent',
  'link', 'image', 'video', 'align', 'float',
  'width', 'height'
];


const FormikForm = ({
  initialValues,
  validationSchema,
  nonFixButton,
  fields,
  onSubmit,
  submitButtonText = "Submit",
  isLoading = false,
  enableReinitialize = true,
}) => {
  const [formKey, setFormKey] = useState(0);
  // const mode = useSelector((state) => state.theme.mode)
  const mode = "light"
  const theme = getAppTheme(mode);
  const hasEmailEditor = fields.some((field) => field.type === "emailEditor");
  useEffect(() => {
    setFormKey((prev) => prev + 1); // Force re-render when initialValues change
  }, [initialValues]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnMount: true,
    enableReinitialize,
  });

  // Helper function to validate file type
  const validateFileType = (file, acceptedTypes) => {
    if (!file || !acceptedTypes) return true;

    // Parse the accept string to get allowed file types
    const types = acceptedTypes.split(',').map(type => type.trim());

    // Check if file extension or mime type is in the allowed list
    const fileType = file.type;
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();


    return types.some(type => {
      if (type.startsWith('.')) {
        // Extension check
        return fileExtension.toLowerCase() === type.toLowerCase();
      } else if (type.includes('/*')) {
        // Mime type category check (e.g., image/*)
        const category = type.split('/')[0];
        return fileType.startsWith(category);
      } else {
        // Exact mime type check
        return fileType === type;
      }
    });
  };

  const { values, setFieldValue } = formik;

  const prevValuesRef = useRef(values);

  useEffect(() => {
    fields.forEach((field) => {
      if (field.type === "logic" && field.dependentOn) {
        const matchedOption = (field.options || []).find(
          (opt) => opt.parentValue === values[field.dependentOn]
        );

        const newValue = matchedOption?.label || "";
        const prevValue = prevValuesRef.current[field.name];

        if (prevValue !== newValue) {
          setFieldValue(field.name, newValue);
        }
      }
    });

    prevValuesRef.current = values;
  }, [values, fields, setFieldValue]);

  const emailEditorRef = useRef(null);

  const exportHtml = () => {
    emailEditorRef.current.editor.exportHtml((data) => {
      const { html } = data;
      // Set HTML into a specific field in Formik values
      formik.setFieldValue("content", html, false); // false prevents triggering validation
      setTimeout(() => {
        formik.handleSubmit();
      }, 0);
    });
  };

  const normalizeQuillContent = (content) => {
    // Strip tags and check if content is empty or just contains empty tags
    const plainText = content.replace(/<(.|\n)*?>/g, "").trim();
    return plainText === "" ? "" : content;
  }

  const onLoad = () => {
    // editor instance is created
    // you can load your template here;
    // const templateJson = {};
    // emailEditorRef.current.editor.loadDesign(templateJson);
  }

  const onReady = () => {
    // editor is ready
    console.log('onReady');
  };

  const renderArrayField = (field, formik) => {
    const { values, setFieldValue, errors, touched } = formik;
    const arrayValues = values[field.name] || [];

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {field.label}
        </Typography>

        {arrayValues.map((item, index) => (
          field.renderItem(index, {
            remove: (idx) => {
              const newArray = [...arrayValues];
              newArray.splice(idx, 1);
              setFieldValue(field.name, newArray);
            },
            push: () => {
              setFieldValue(field.name, [...arrayValues, field.emptyItem]);
            },
            values,
            form: formik // Pass the entire formik object for validation access
          })
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          fullWidth
          onClick={() => {
            // If custom add handler exists, use it
            if (field.onAddItem) {
              field.onAddItem(values, setFieldValue);
            } else {
              // Default behavior
              setFieldValue(field.name, [...arrayValues, field.emptyItem]);
            }
          }}
          sx={{ mt: 2 }}
        >
          {field.addButtonText || `Add ${field.itemLabel}`}
        </Button>

        {touched[field.name] && errors[field.name] && typeof errors[field.name] === 'string' && (
          <Typography color="error" variant="caption">
            {errors[field.name]}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <form onSubmit={(e) => {
        e.preventDefault();
        if (hasEmailEditor) {
          exportHtml(); // triggers email editor export and handles submission
        } else {
          formik.handleSubmit(); // normal submit
        }
      }}
        key={formKey}
        style={{ paddingBottom: "60px" }}>
        {fields.map((field) => (
          // Check if field should be rendered based on renderCondition
          (!field.renderCondition || field.renderCondition(formik.values)) && (
            <Box key={field.name} sx={{ mb: 2 }}>
            {field.type === "array" ? (
              renderArrayField(field, formik)
            ) : field.type === "avatar" ? (
              <Box sx={{ textAlign: "center", marginBottom: 3, position: "relative" }}>
                <Avatar
                  src={(() => {
                    // If no value is provided (null, undefined, empty string)
                    if (!formik.values[field.name]) {
                      return "/profile.jpg";
                    }

                    // If it's a base64 image (from newly uploaded file)
                    if (typeof formik.values[field.name] === 'string' &&
                      formik.values[field.name].startsWith('data:image')) {
                      return formik.values[field.name];
                    }

                    // For any other string value
                    return BEBaseRoot.slice(0, -1) + formik.values[field.name];
                  })()}
                  alt={field.label}
                  sx={{ width: 120, height: 120, margin: "0 auto" }}
                />
                <Box sx={{
                  position: "absolute",
                  bottom: 0,
                  right: "50%",
                  transform: "translateX(60px)",
                  display: "flex"
                }}>
                  <IconButton
                    component="label"
                    sx={{
                      backgroundColor: "#e4b648",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#d4a238" },
                      marginRight: 1,
                      width: 36,
                      height: 36
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={async (event) => {
                        const file = event.currentTarget.files[0];
                        if (file) {
                          try {
                            // Resize the image before setting it in the form
                            const resizedFile = await resizeProfileImage(file);

                            // Create a preview URL for display
                            const reader = new FileReader();
                            reader.onload = () => {
                              formik.setFieldValue(field.name, reader.result);
                              if (field.fileField) {
                                formik.setFieldValue(field.fileField, resizedFile);
                              }
                            };
                            reader.readAsDataURL(resizedFile);
                          } catch (error) {
                            console.error("Error resizing image:", error);
                            // Fallback to original file if resize fails
                            const reader = new FileReader();
                            reader.onload = () => {
                              formik.setFieldValue(field.name, reader.result);
                              if (field.fileField) {
                                formik.setFieldValue(field.fileField, file);
                              }
                            };
                            reader.readAsDataURL(file);
                          }
                        }
                      }}
                    />
                  </IconButton>
                  {formik.values[field.name] && (
                    <IconButton
                      onClick={() => {
                        // Set to null (not empty string) when deleting
                        formik.setFieldValue(field.name, null);
                        if (field.fileField) {
                          formik.setFieldValue(field.fileField, null);
                        }
                      }}
                      sx={{
                        backgroundColor: "#f44336",
                        color: "#fff",
                        "&:hover": { backgroundColor: "#d32f2f" },
                        width: 36,
                        height: 36
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
                {formik.touched[field.name] && formik.errors[field.name] && (
                  <Typography variant="caption" color="error">
                    {formik.errors[field.name]}
                  </Typography>
                )}
              </Box>
            ) : field.type === "select" ? (
              <FormControl
                fullWidth
                error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                disabled={field.disabled}
              >
                <InputLabel id={`${field.name}-label`}>{field.label}</InputLabel>
                <Select
                  labelId={`${field.name}-label`}
                  id={field.name}
                  name={field.name}
                  value={formik.values[field.name] || ""}
                  label={field.label}
                  onChange={(e) => {
                    // Call the default handleChange first
                    formik.handleChange(e);

                    // Then call field-specific onChange if provided
                    if (field.onChange) {
                      field.onChange(e.target.value, formik.setFieldValue);
                    }
                  }}
                  onBlur={formik.handleBlur}
                >
                  {field.options?.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} disabled={opt.isDisabled === true}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
                {field.additional}
                {formik.touched[field.name] && formik.errors[field.name] && (
                  <Typography variant="caption" color="error">
                    {formik.errors[field.name]}
                  </Typography>
                )}
              </FormControl>
            ) : field.type === "autocomplete" ? (
              <>
                <FormControl
                  fullWidth
                  error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                  disabled={field.disabled}
                >
                  <Autocomplete
                    id={field.name}
                    options={field.options || []}
                    getOptionLabel={field.getOptionLabel || ((option) => option?.label || "")}
                    isOptionEqualToValue={field.isOptionEqualToValue}
                    value={formik.values[field.name]}
                    onChange={(_, newValue) => formik.setFieldValue(field.name, newValue)}
                    disabled={field.disabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                        helperText={formik.touched[field.name] && formik.errors[field.name]}
                        onBlur={formik.handleBlur}
                      />
                    )}
                  />
                  {field.additional}
                </FormControl>
              </>
            ) : field.type === "file" ? (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {field.label}
                  </Typography>
                  {/* Image Preview Section */}
                  {(formik.values[field.name] || field.currentFile) && field.accept?.includes('image') && (
                    <Box sx={{ mb: 1, position: 'relative', width: '100%', height: 200, border: '1px dashed grey', borderRadius: 2, overflow: 'hidden' }}>
                      <img
                        src={
                            formik.values[field.name] instanceof File
                            ? URL.createObjectURL(formik.values[field.name])
                            : field.currentFile
                        }
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                       <IconButton
                        size="small"
                        onClick={() => formik.setFieldValue(field.name, null)}
                        sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    type="file"
                    id={field.name}
                    name={field.name}
                    inputProps={{ accept: field.accept || "*" }}
                    onChange={(event) => {
                      formik.setFieldValue(field.name, event.currentTarget.files[0]);
                    }}
                    error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                    helperText={formik.touched[field.name] && formik.errors[field.name]}
                  />
                  {!(formik.values[field.name] || field.currentFile) && field.currentFile && (
                     <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                       Current file: {field.currentFile.split('/').pop()}
                     </Typography>
                  )}
                </Box>
            ) : field.type === "logic" ? (
              <TextField
                fullWidth
                id={field.name}
                name={field.name}
                label={field.label}
                value={
                  field.dependentOn
                    ? (field.options || []).find((opt) => opt.parentValue === formik.values[field.dependentOn])?.label || ""
                    : formik.values[field.name] || ""
                }
                InputProps={{ readOnly: true }}
                variant="outlined"
              />
            ) : field.type === "textarea" ? (
              <ThemeProvider theme={theme}>
                <ReactQuill
                  value={formik.values[field.name] || ""}
                  label={field.label}
                  className={mode === 'dark' ? 'quill-dark' : 'quill-light'}
                  placeholder={field.label}
                  onChange={(content) => {
                    formik.setFieldValue(field.name, normalizeQuillContent(content));
                  }}
                  formats={formats}
                  modules={{
                    imageActions: {},
                    imageFormats: {},
                    toolbar: [
                      [{ header: [1, 2, 3, false] }],
                      [{ font: [] }],
                      [{ size: [] }],
                      ["bold", "italic", "underline", "strike"],
                      [{ color: [] }, { background: [] }],
                      [{ script: "sub" }, { script: "super" }],
                      [{ list: "ordered" }, { list: "bullet" }],
                      [{ align: [] }],
                      ["link", "image", "video"],
                      ["clean"],
                    ]
                  }}
                  onBlur={() => formik.setFieldTouched(field.name, true)}
                />
                {formik.touched[field.name] && formik.errors[field.name] && (
                  <FormHelperText sx={{color: "red"}}>{formik.errors[field.name]}</FormHelperText>
                )}
              </ThemeProvider>
            ) : field.type === "emailEditor" ? (
              <ThemeProvider theme={theme}>
                <EmailEditor
                  ref={emailEditorRef}
                  onLoad={onLoad}
                  onReady={onReady}
                />
              </ThemeProvider>
            ) : field.type === "file" ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  {field.label}
                </Typography>
                <TextField
                  fullWidth
                  type="file"
                  id={field.name}
                  name={field.name}
                  inputProps={{
                    accept: field.accept || "*",
                    "aria-label": field.label,
                  }}
                  onChange={(event) => {
                    const file = event.currentTarget.files[0];
                    if (file && field.accept) {
                      const isValidType = validateFileType(file, field.accept);
                      if (!isValidType) {
                        formik.setFieldError(field.name, `Invalid file type. Supported: ${field.accept}`);
                      } else {
                        formik.setFieldValue(field.name, file);
                      }
                    } else {
                      formik.setFieldValue(field.name, file);
                    }
                  }}
                  error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                  helperText={formik.touched[field.name] && formik.errors[field.name]}
                  disabled={field.disabled}
                />
                {field.currentFile && (
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                    Current file: {field.currentFile}
                  </Typography>
                )}
              </Box>
            ) : field.type === "date" ? (
              <DatePicker
                label={field.label}
                value={formik.values[field.name] ? dayjs(formik.values[field.name]) : null}
                onChange={(date) => formik.setFieldValue(field.name, date ? date.format("YYYY-MM-DD") : "")}
                maxDate={field.name === "dob" ? dayjs() : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: formik.touched[field.name] && Boolean(formik.errors[field.name]),
                    helperText: formik.touched[field.name] && formik.errors[field.name],
                    onBlur: () => formik.setFieldTouched(field.name, true),
                  },
                }}
                disabled={field.disabled}
              />
            ) : field.type === "h4" ? (
              <Typography variant="h4" gutterBottom>
                {field.label}
              </Typography>
            ) : field.type === "checkbox" ? (
              <FormControl
                error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                component="fieldset"
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={String(formik.values[field.name]) === "true"} // Fix: always boolean
                      onChange={(event) =>
                        formik.setFieldValue(field.name, event.target.checked)
                      }
                    />
                  }
                  label={field.label}
                />
                {formik.touched[field.name] && formik.errors[field.name] && (
                  <FormHelperText>{formik.errors[field.name]}</FormHelperText>
                )}
              </FormControl>



            ) : (
              <TextField
                fullWidth
                id={field.name}
                name={field.name}
                label={field.label}
                type={field.type || "text"}
                value={formik.values[field.name] || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                helperText={formik.touched[field.name] && formik.errors[field.name]}
                disabled={field.disabled}
                InputProps={field.InputProps}
              />
            )}
          </Box>
          )
        ))}
        {nonFixButton ? (
          <Box sx={{ display: "flex", justifyContent: "flex-start", flexWrap: "wrap", width: "100%", bottom: 0, zIndex: 100, marginTop: "30px", boxShadow: "0 0 0 rgba(0,0,0,0.5)" }}>

            {hasEmailEditor ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={exportHtml} // You can call a different function here
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : "Send Email"}
              </Button>
            ) : (
              <Button type="submit" variant="contained" color="primary" disabled={!formik.isValid || formik.isSubmitting || !formik.dirty || isLoading}>
                {isLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </Box>
                ) : (
                  submitButtonText
                )}
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "flex-start", flexWrap: "wrap", position: "fixed", width: "100%", bottom: 0, padding: "30px", zIndex: 100, marginTop: "30px", boxShadow: "0 0 0 rgba(0,0,0,0.5)" }}>

            {hasEmailEditor ? (
              <Button
                variant="contained"
                color="secondary"
                onClick={exportHtml} // You can call a different function here
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : "Send Email"}
              </Button>
            ) : (
              <Button type="submit" variant="contained" color="primary" disabled={!formik.isValid || formik.isSubmitting || isLoading}>
                {isLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Processing...
                  </Box>
                ) : (
                  submitButtonText
                )}
              </Button>
            )}
          </Box>
        )}
      </form>
    </LocalizationProvider >
  );
};

FormikForm.propTypes = {
  initialValues: PropTypes.object.isRequired,
  validationSchema: PropTypes.object.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      options: PropTypes.array,
      disabled: PropTypes.bool,
      getOptionLabel: PropTypes.func,
      isOptionEqualToValue: PropTypes.func,
      currentFile: PropTypes.string,
      rows: PropTypes.number,
      InputProps: PropTypes.object,
      defaultImage: PropTypes.string,
      fileField: PropTypes.string,
    })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitButtonText: PropTypes.string,
  isLoading: PropTypes.bool,
  enableReinitialize: PropTypes.bool,
};

export default FormikForm;